import {
  getHighestRole,
  isApproverOrHigherRole,
  isOperationalRole,
  isPrivilegedRole,
} from '@ankor-io/common/domain/iam/account'
import { Role } from '@ankor-io/common/types/account'
import { CharterStatus } from '@ankor-io/common/types/charter'
import { QuickBooksSyncStatus } from '@ankor-io/common/types/integrations/quickbooks'
import { XeroConnectionSyncStatus } from '@ankor-io/common/types/integrations/xero/types'
import {
  AWXCardActivityEventStatus,
  type FullTransactionPayload,
  PaymentMethod,
  type Transaction,
  TransactionStatus,
  TransactionType,
} from '@ankor-io/common/types/transaction/transaction'

export type OpsSyncAwareTransaction = {
  xeroSync?: { status: XeroConnectionSyncStatus }
  qboSync?: { status: QuickBooksSyncStatus }
}

/**
 * Operations expenses only: after a successful Xero or QuickBooks sync the expense must not be edited or approved/rejected.
 * APA is unchanged by this rule.
 */
export const isOpsExpenseImmutableAfterSuccessfulSync = (transaction: OpsSyncAwareTransaction): boolean =>
  transaction.xeroSync?.status === XeroConnectionSyncStatus.COMPLETED ||
  transaction.qboSync?.status === QuickBooksSyncStatus.COMPLETED

/**
 * Transfer payment expenses can only be edited before the transfer has been paid out (no AWX activity yet)
 * unless the user is a privileged role (OWNER/ADMIN)
 */
export const canEditTransferPaymentMethodExpense = (
  transaction: {
    paymentMethod: PaymentMethod
    awxActivityEvent?: unknown
  },
  role: Role,
): boolean =>
  transaction.paymentMethod !== PaymentMethod.TRANSFER || !transaction.awxActivityEvent || isPrivilegedRole(role)

/**
 * Determines if a user can edit a specific transaction.
 *
 * Business Rules:
 * 1. Admin-like users (VESSEL_ADMIN/ADMIN/OWNER) can edit any non-gratuity transaction
 * 2. Non-privileged users (SPENDER/APPROVER) can only edit:
 *    - Their own transactions (createdBy === user.id)
 *    - That are not yet approved
 * 3. Gratuity transactions cannot be edited by anyone
 *
 * Note: VESSEL_ADMIN gets admin-like edit capability here. It is vessel-scoped, but the UI
 * only ever surfaces transactions on vessels the user can access, and the edit endpoint
 * independently re-verifies vessel access, so admin-equivalence in the UI is safe.
 *
 * @param transaction - The transaction to check edit permissions for
 * @param user - The current user attempting the action
 * @param userRole - The user's role in the organization
 * @returns True if the user can edit the transaction, false otherwise
 */
export const canUserEditTransaction = (
  transaction: Transaction,
  user: { id: string } | null | undefined,
  userRole: Role,
): boolean => {
  // Gratuity transactions cannot be edited by anyone
  if (transaction.type === TransactionType.GRATUITY) {
    return false
  }

  // Admin-like users (VESSEL_ADMIN/ADMIN/OWNER) can edit any non-gratuity transaction
  if (isOperationalRole(userRole)) {
    return true
  }

  // Non-privileged users can only edit their own unapproved transactions
  return (
    (userRole === Role.SPENDER || userRole === Role.APPROVER) &&
    transaction.createdBy === user?.id &&
    transaction.status !== TransactionStatus.APPROVED
  )
}

/** OPS module: same as {@link canUserEditTransaction} but blocks edits after a successful Xero or QuickBooks sync. */
export const canUserEditOpsTransaction = (
  transaction: Transaction & OpsSyncAwareTransaction,
  user: { id: string } | null | undefined,
  userRole: Role,
): boolean =>
  !isOpsExpenseImmutableAfterSuccessfulSync(transaction) && canUserEditTransaction(transaction, user, userRole)

/** OPS module: same as {@link canUserApproveTransaction} but blocks after a successful Xero or QuickBooks sync. */
export const canUserApproveOpsTransaction = (
  transaction: Transaction & OpsSyncAwareTransaction,
  userRole: Role,
): boolean => !isOpsExpenseImmutableAfterSuccessfulSync(transaction) && canUserApproveTransaction(transaction, userRole)

/** OPS module: same as {@link canUserPerformTransactionActions} but hides all actions after a successful Xero or QuickBooks sync. */
export const canUserPerformOpsTransactionActions = (
  transaction: Transaction & OpsSyncAwareTransaction,
  user: { id: string } | null | undefined,
  userRole: Role,
): boolean =>
  !isOpsExpenseImmutableAfterSuccessfulSync(transaction) &&
  canUserPerformTransactionActions(transaction, user, userRole)

/**
 * Determines if a user can approve or reject a specific transaction.
 *
 * Business Rules:
 * 1. Only approver-level roles and above can approve/reject
 * 2. Transaction must be ready for review (SUBMITTED status)
 * 3. Cannot approve/reject already approved or rejected transactions
 *
 * @param transaction - The transaction to check approval permissions for
 * @param userRole - The user's role in the organization
 * @returns True if the user can approve/reject the transaction, false otherwise
 */
export const canUserApproveTransaction = (transaction: Transaction, userRole: Role): boolean => {
  // Must be approver or higher role
  if (!isApproverOrHigherRole(userRole)) {
    return false
  }

  // Transaction must be ready for review (SUBMITTED status)
  if (transaction.status !== TransactionStatus.SUBMITTED) {
    return false
  }

  return true
}

/**
 * Determines if a user can perform any actions on a specific transaction.
 * Shows the actions menu when the user can approve/reject, or edit their own unapproved expense.
 * Admin-like roles can act on any non gratuity transaction.
 * Gratuity transactions cannot have actions performed on them by anyone.
 *
 * @param transaction - The transaction to check action permissions for
 * @param user - The current user attempting the action
 * @param userRole - The user's role in the organization
 * @returns True if the user can perform any actions on the transaction, false otherwise
 */
export const canUserPerformTransactionActions = (
  transaction: Transaction,
  user: { id: string } | null | undefined,
  userRole: Role,
): boolean => {
  // Gratuity transactions cannot have actions performed on them by anyone
  if (transaction.type === TransactionType.GRATUITY) {
    return false
  }

  const cardTransaction = transaction as FullTransactionPayload
  if (cardTransaction?.awxActivityEvent?.status === AWXCardActivityEventStatus.FAILED) {
    return false
  }

  // Admin-like users (VESSEL_ADMIN/ADMIN/OWNER) can perform actions on any non-gratuity transaction
  if (isOperationalRole(userRole)) {
    return true
  }

  // Approvers cannot perform actions on already approved transactions
  if (getHighestRole([userRole]) === Role.APPROVER && transaction.status === TransactionStatus.APPROVED) {
    return false
  }

  // Approve/reject when eligible, otherwise only own unapproved expenses (avoids empty menus)
  return (
    canUserApproveTransaction(transaction, userRole) ||
    ((userRole === Role.SPENDER || userRole === Role.APPROVER) &&
      transaction.createdBy === user?.id &&
      transaction.status !== TransactionStatus.APPROVED)
  )
}

/**
 * Aligns with pay-service manual transaction deletion: manual type only, not approved or rejected.
 */
export const isDeletableTransaction = (transaction: { type: TransactionType; status: TransactionStatus }): boolean =>
  transaction.type === TransactionType.MANUAL &&
  transaction.status !== TransactionStatus.APPROVED &&
  transaction.status !== TransactionStatus.REJECTED

/**
 * Charters a user may attach a manual APA expense to.
 * Signed-off charters accept new expenses only from OWNER and ADMIN, matching
 * AllowCharterTransactionMiddleware.
 */
export const charterStatusesAllowedForManualExpense = (role: Role): CharterStatus[] => {
  const openStatuses = [CharterStatus.UPCOMING, CharterStatus.IN_PROGRESS]
  if (isPrivilegedRole(role)) {
    // Privileged users can attach manual expenses to signed-off charters
    return [...openStatuses, CharterStatus.SIGNED_OFF]
  }

  // But other users cannot attach manual expenses to signed-off charters and will hit permission issues in the middleware
  return openStatuses
}

/** OPS: same as {@link isDeletableTransaction} but blocked after a successful Xero or QuickBooks sync. */
export const canUserDeleteOpsTransaction = (
  transaction: { type: TransactionType; status: TransactionStatus } & OpsSyncAwareTransaction,
): boolean => isDeletableTransaction(transaction) && !isOpsExpenseImmutableAfterSuccessfulSync(transaction)
